import React, { useState, useRef, useEffect, useCallback } from 'react';
import zedHero from '../images/zed-hero.jpg';
import zedBadges from '../images/zed.png';

/* ── Floating Video Widget ── */
// Place your video file at: public/zed-promo.mp4
// It will be served at /zed-promo.mp4 by React's dev server & Netlify

const VIDEO_SRC = '/zed-promo.mp4'; // ← replace filename if different

function FloatingVideo() {
  const [visible, setVisible] = useState(true);

  // Default: fixed bottom-left, like reference image
  // pos stores { left, bottom } in fixed viewport coords
  const [pos] = useState({ left: 16, bottom: 16 });
  // Track if user has dragged (switch to top/left coords after first drag)
  const [dragged, setDragged] = useState(false);
  const [absPos, setAbsPos] = useState({ x: 16, y: null }); // y=null means use bottom

  const [size, setSize] = useState({ w: 175, h: 290 }); // portrait, like reference
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);

  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const widgetRef = useRef(null);

  // Compute current fixed position for drag start
  const getCurrentFixedPos = useCallback(() => {
    if (widgetRef.current) {
      const r = widgetRef.current.getBoundingClientRect();
      return { x: r.left, y: r.top };
    }
    return { x: 16, y: window.innerHeight - size.h - 16 };
  }, [size.h]);

  const onMouseDownDrag = useCallback((e) => {
    if (e.target.closest('.fv-resize-handle') || e.target.closest('.fv-close')) return;
    e.preventDefault();
    const cur = getCurrentFixedPos();
    dragOffset.current = { x: e.clientX - cur.x, y: e.clientY - cur.y };
    setDragging(true);
  }, [getCurrentFixedPos]);

  // Touch support for drag
  const onTouchStartDrag = useCallback((e) => {
    if (e.target.closest('.fv-resize-handle') || e.target.closest('.fv-close')) return;
    const touch = e.touches[0];
    const cur = getCurrentFixedPos();
    dragOffset.current = { x: touch.clientX - cur.x, y: touch.clientY - cur.y };
    setDragging(true);
  }, [getCurrentFixedPos]);

  const onMouseDownResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStart.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    setResizing(true);
  }, [size]);

  useEffect(() => {
    if (!dragging && !resizing) return;

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      if (dragging) {
        const newX = Math.max(0, Math.min(window.innerWidth - size.w, clientX - dragOffset.current.x));
        const newY = Math.max(0, Math.min(window.innerHeight - size.h, clientY - dragOffset.current.y));
        setAbsPos({ x: newX, y: newY });
        setDragged(true);
      }
      if (resizing) {
        const dw = clientX - resizeStart.current.x;
        const dh = clientY - resizeStart.current.y;
        setSize({
          w: Math.max(140, Math.min(400, resizeStart.current.w + dw)),
          h: Math.max(200, Math.min(600, resizeStart.current.h + dh)),
        });
      }
    };

    const onUp = () => { setDragging(false); setResizing(false); };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, resizing, size.w, size.h]);

  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    setMuted((m) => {
      if (videoRef.current) videoRef.current.muted = !m;
      return !m;
    });
  }, []);

  if (!visible) return null;

  // Style: fixed to viewport. Default = bottom-left. After drag = top-left with computed coords.
  const fixedStyle = dragged
    ? { position: 'fixed', left: absPos.x, top: absPos.y, width: size.w, height: size.h }
    : { position: 'fixed', left: pos.left, bottom: pos.bottom, width: size.w, height: size.h };

  return (
    <div
      ref={widgetRef}
      className={`fv-widget${dragging ? ' fv-dragging' : ''}`}
      style={fixedStyle}
      onMouseDown={onMouseDownDrag}
      onTouchStart={onTouchStartDrag}
    >
      {/* Header bar */}
      <div className="fv-header">
        <span className="fv-live-badge">
          <span className="fv-live-dot" />
          ZED
        </span>
        <div className="fv-header-actions">
          <button className="fv-mute-btn" onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
            {muted ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            )}
          </button>
          <button className="fv-close" onClick={() => setVisible(false)} title="Close">✕</button>
        </div>
      </div>

      {/* Video */}
      <div className="fv-video-area">
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          autoPlay
          loop
          muted
          playsInline
          className="fv-video"
        />
        {/* Bottom label */}
        <div className="fv-overlay-label">
          ZED Certification — MSME India
        </div>
      </div>

      {/* Resize handle — bottom-right corner */}
      <div
        className="fv-resize-handle"
        onMouseDown={onMouseDownResize}
        title="Drag to resize"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M11 1L1 11M11 6L6 11M11 11" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
}

/* ── Lead Form ── */
function LeadForm() {
  const [form, setForm] = useState({
    name: '', company: '', phone: '', email: '', state: '', service: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formType: 'zed', ...form }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        alert('Something went wrong. Please try again or call us directly on +91 92161 83238.');
      }
    } catch {
      alert('Something went wrong. Please try again or call us directly on +91 92161 83238.');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="zed-form-success">
        <div className="zed-form-success-icon">✅</div>
        <h3>Thank You!</h3>
        <p>We've received your enquiry. Our ZED expert will contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <div className="zed-lead-form-wrap">
      <div className="zed-form-header">
        <h3>Get ZED Certified</h3>
        <p>Fill in your details — our expert will reach out within 24 hours.</p>
      </div>
      <form onSubmit={submit} className="zed-form">
        <div className="zf-row">
          <div className="zf-field">
            <label>Full Name *</label>
            <input name="name" value={form.name} onChange={handle} placeholder="Your name" required />
          </div>
          <div className="zf-field">
            <label>Company / MSME Name *</label>
            <input name="company" value={form.company} onChange={handle} placeholder="Your company name" required />
          </div>
        </div>
        <div className="zf-row">
          <div className="zf-field">
            <label>Phone Number *</label>
            <input name="phone" type="number" value={form.phone} onChange={handle} placeholder="+91 XXXXX XXXXX" required />
          </div>
          <div className="zf-field">
            <label>Email Address *</label>
            <input name="email" type="email" value={form.email} onChange={handle} placeholder="you@company.com" required />
          </div>
        </div>
        <div className="zf-row">
          <div className="zf-field">
            <label>State</label>
            <input name="state" value={form.state} onChange={handle} placeholder="e.g. Haryana" />
          </div>
          <div className="zf-field">
            <label>Interested In</label>
            <select name="service" value={form.service} onChange={handle}>
              <option value="">Select level</option>
              <option>ZED Bronze Certification</option>
              <option>ZED Silver Certification</option>
              <option>ZED Gold Certification</option>
              <option>Not Sure — Need Guidance</option>
            </select>
          </div>
        </div>
        <button type="submit" className="zf-submit" disabled={loading}>
          {loading ? 'Submitting…' : 'Apply Now →'}
        </button>
        <p className="zf-note">🔒 Your information is safe and will never be shared.</p>
      </form>
    </div>
  );
}

/* ── ZED Process Steps ── */
const STEPS = [
  { num: '01', icon: '📝', title: 'Take the ZED Pledge', desc: 'Register on the ZED portal and take the ZED Pledge — a solemn commitment to uphold Zero Defect Zero Effect values. Receive a ₹10,000 joining reward instantly.' },
  { num: '02', icon: '🎯', title: 'Select Certification Level', desc: 'Choose Bronze (7 parameters), Silver (14 parameters), or Gold (20 parameters) based on your enterprise\'s readiness and ambition.' },
  { num: '03', icon: '📸', title: 'Submit Evidence via ZED App', desc: 'Upload photographs and documents as evidence of compliance through the ZED MSME App on your mobile device.' },
  { num: '04', icon: '🔍', title: 'Offsite / Onsite Assessment', desc: 'An AQMCS ZED assessor conducts a remote offsite review or an onsite visit to verify evidence and assess your MSME.' },
  { num: '05', icon: '🔧', title: 'Close Non-Conformities', desc: 'Any non-conformities raised by the assessor are addressed, corrective actions implemented and uploaded through the App.' },
  { num: '06', icon: '🏆', title: 'ZED Certificate Issued', desc: 'Upon satisfactory closure of all requirements, your ZED Certificate (Bronze / Silver / Gold) is issued by the Ministry of MSME.' },
];

/* ── Benefits ── */
const BENEFITS = [
  { icon: '💰', title: 'Up to 80% Subsidy on Certification', desc: 'Micro Enterprises get 80%, Small Enterprises 60%, and Medium Enterprises 50% subsidy on ZED certification fees.' },
  { icon: '🎁', title: '₹10,000 Joining Reward', desc: 'Every MSME that takes the ZED Pledge gets a ₹10,000 joining reward — effectively making Bronze certification free.' },
  { icon: '🧪', title: 'Testing & Quality Certification Subsidy', desc: 'Reimburse up to 75% of testing or quality certification fees (ISO, BIS, NABL etc.) with a maximum ceiling of ₹50,000.' },
  { icon: '🏦', title: 'Easier Bank Credit', desc: 'ZED certified MSMEs benefit from reduced processing fees and lower interest rates from 17+ participating banks.' },
  { icon: '📈', title: 'Government Procurement Preference', desc: 'Priority in government tenders and procurement for ZED certified units, boosting domestic market opportunities.' },
  { icon: '🌍', title: 'Export Market Readiness', desc: 'ZED certification signals global quality standards, opening doors to international markets and export credibility.' },
  { icon: '♻️', title: 'Sustainable Manufacturing', desc: 'Adopt energy-efficient, waste-reducing, and environmentally responsible practices aligned with global sustainability goals.' },
  { icon: '📊', title: 'Handholding & Capacity Building', desc: 'QCI-approved consultants provide 4-month handholding support to help MSMEs upgrade from Bronze to Silver or Gold.' },
];

/* ── Levels ── */
const LEVELS = [
  {
    name: 'Bronze',
    emoji: '🥉',
    color: '#cd7f32',
    bg: '#fdf4eb',
    border: '#cd7f32',
    params: '7 Parameters',
    desc: '',
    perks: [
      'Leadership',
      'Swachh Workplace',
      'Occupational (Workplace) Safety ',
      'Measurement of Timely Delivery',
      'Quality Management',
      'Energy Management',
      'Measurement and Analysis',
    ],
  },
  {
    name: 'Silver',
    emoji: '🥈',
    color: '#6b7280',
    bg: '#f5f5f5',
    border: '#9ca3af',
    params: '14 Parameters',
    desc: '',
    perks: [
      'All 7 Bronze parameters',
      'Human Resource Management',
      'Daily Works Management',
      'Planned Maintenance & Calibration',
      'Process Control',
      'Product Quality & Safety (Testing/Certification)',
      'Material Management',
      'Environment Management',
    ],
  },
  {
    name: 'Gold',
    emoji: '🥇',
    color: '#b8860b',
    bg: '#fffbeb',
    border: '#d4a017',
    params: '20 Parameters',
    desc: '',
    perks: [
      'All 14 Silver parameters',
      'Supply Chain Management',
      'Risk Management',
      'Waste Management (Muda, Mura, Muri)',
      'Technology Selection & Upgradation ',
      'Natural Resource Conservation',
      'Corporate Social Responsibility',
    ],
  },
];

/* ── Subsidy Table ── */
const SUBSIDY = [
  { type: 'Micro Enterprise', bronze: '80%', silver: '80%', gold: '80%' },
  { type: 'Small Enterprise', bronze: '60%', silver: '60%', gold: '60%' },
  { type: 'Medium Enterprise', bronze: '50%', silver: '50%', gold: '50%' },
  { type: 'Women / SC / ST / NER / Aspirational Districts', bronze: '+10%', silver: '+10%', gold: '+10%' },
  { type: 'SFURTI / MSE-CDP Cluster MSMEs', bronze: '+5%', silver: '+5%', gold: '+5%' },
];

/* ── FAQs ── */
const FAQS = [
  { q: 'What is MSME Sustainable (ZED) Certification?', a: 'ZED stands for Zero Defect Zero Effect. It is a Government of India initiative by the Ministry of MSME to encourage manufacturing MSMEs to produce goods with zero defects while having zero effect on the environment. It is based on a 20-parameter model covering quality, safety, energy, sustainability, and more.' },
  { q: 'Who is eligible for ZED Certification?', a: 'All manufacturing MSMEs registered on the Udyam Registration Portal of the Ministry of MSME are eligible to apply for ZED Certification. Each unit under one Udyam Registration needs to apply separately to avail subsidies.' },
  { q: 'What are the three levels of ZED Certification?', a: 'Bronze (7 parameters), Silver (14 parameters), and Gold (20 parameters). An MSME can apply for any level directly after taking the ZED Pledge, without having to start from Bronze.' },
  { q: 'What is the ZED Pledge?', a: 'The ZED Pledge is a mandatory pre-commitment by an MSME to uphold the values of Zero Defect Zero Effect. Upon taking the pledge, the MSME receives a ₹10,000 joining reward (valid for 1 year) and immediate access to WASH Standard certification and MSME KAWACH capacity building programmes.' },
  { q: 'How much government subsidy is available?', a: 'Micro Enterprises get 80%, Small Enterprises 60%, and Medium Enterprises 50% subsidy on certification fees. An additional 10% subsidy is available for Women/SC/ST-owned MSMEs and those in NER, Himalayan, LWE, island territories, or aspirational districts. A further 5% is available for SFURTI/MSE-CDP cluster MSMEs.' },
  { q: 'Is Bronze certification free for Micro MSMEs?', a: 'Effectively yes. Bronze certification costs ₹10,000. With the ₹10,000 joining reward (received on taking the ZED Pledge), the net cost becomes zero for MSMEs, making Bronze certification essentially free.' },
  { q: 'What existing certifications provide exemptions in ZED assessment?', a: 'MSMEs holding certifications like ISO 9001, ISO 14001, ISO 50001, ISO 45001, and other recognised standards may be exempted from assessment on relevant ZED parameters as per the scheme guidelines.' },
  { q: 'Why choose AQMCS for ZED Assessment?', a: 'AQMCS is empanelled with QCI as an accredited ZED Assessment Agency. Our experienced professional provide end-to-end support — from ZED Pledge to certificate issuance — with onsite and offsite assessment capability across India.' },
];

function FaqBlock() {
  const [open, setOpen] = useState(null);
  return (
    <div className="zed-faq-list">
      {FAQS.map((f, i) => (
        <div key={i} className={`zed-faq-item${open === i ? ' open' : ''}`} onClick={() => setOpen(open === i ? null : i)}>
          <div className="zed-faq-q">
            <span>{f.q}</span>
            <span className="zed-faq-arrow">{open === i ? '−' : '+'}</span>
          </div>
          {open === i && <div className="zed-faq-a">{f.a}</div>}
        </div>
      ))}
    </div>
  );
}

/* ── Main Page ── */
export default function ZedPage({ setPage }) {
  return (
    <>
    <FloatingVideo />
    <main className="page" id="main-content">

      {/* ── HERO + LEAD FORM ── */}
      <section className="zed-hero">
        <div className="zed-hero-inner">
          {/* Left */}
          <div className="zed-hero-left">
            <h1 className="zed-hero-title">
              ZED Certification for MSMEs — MSME Sustainable<br />
              <span className="zed-accent">(ZED) Certification</span>
            </h1>
            <p className="zed-hero-sub">
              Zero Defect. Zero Effect. Transform your manufacturing MSME with India's most
              impactful quality & sustainability scheme — assessed by AQMCS, an empanelled
              ZED Assessment Agency with QCI, accredited by NABCB as type-A inspection body.
            </p>
            <div className="zed-hero-pills">
              <span className="zed-pill green">Up to 80% Govt. Subsidy</span>
              <span className="zed-pill blue">₹10,000 Joining Reward</span>
              <span className="zed-pill gold">Bronze · Silver · Gold</span>
            </div>
            <img src={zedHero} alt="ZED Certification Scheme — Zero Defect Zero Effect for MSMEs India" className="zed-hero-img" />
          </div>

          {/* Right — Lead Form */}
          <div className="zed-hero-right">
            <LeadForm />
          </div>
        </div>
      </section>

      {/* ── ABOUT ZED ── */}
      <section className="section" style={{ background: 'var(--off-white)' }}>
        <div className="sec-inner">
          <div className="zed-about-grid">
            <div>
              <div className="sec-tag">About ZED</div>
              <h2 className="sec-title">Zero Defect. Zero Effect.</h2>
              <p className="sec-lead" style={{ marginBottom: '1.5rem' }}>
                Addressing the nation on India's 68th Independence Day, Hon'ble Prime Minister
                Shri Narendra Modi urged MSMEs to manufacture goods with "zero defects" and ensure
                goods have "zero effect" on the environment.
              </p>
              <p style={{ fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.8, marginBottom: '1rem' }}>
                The MSME Sustainable (ZED) Certification scheme is an extensive Government of India
                initiative to enhance global competitiveness of MSMEs by motivating and incentivising
                them to adopt ZED practices across quality, environment, energy, safety, and more.
                The scheme covers all sectors of manufacturing MSMEs.
              </p>
              <p style={{ fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.8, marginBottom: '1rem' }}>
                The ZED Model is built on <strong>20 parameters</strong> across three progressive
                certification levels — Bronze, Silver, and Gold. MSMEs with existing certifications
                such as ISO 9001, ISO 14001, ISO 50001, ISO 45001 may receive exemptions from relevant
                parameters under the scheme.
              </p>
              <p style={{ fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.8 }}>
                AQMCS is empanelled with QCI as a ZED Assessment Agency, offering professional,
                end-to-end assessment services to manufacturing MSMEs across India.
              </p>
            </div>
            <div className="zed-badges-img-wrap">
              <img src={zedBadges} alt="ZED Bronze Silver Gold Certification Levels" className="zed-badges-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ── CERTIFICATION LEVELS ── */}
      <section className="section">
        <div className="sec-inner">
          <div className="sec-tag">Certification Levels</div>
          <h2 className="sec-title">Three Levels of ZED Certification</h2>
          <p className="sec-lead">
            After taking the ZED Pledge, your MSME can apply directly for any level — Bronze, Silver, or Gold —
            based on its readiness. Each level builds on the previous one.
          </p>
          <div className="zed-levels-grid">
            {LEVELS.map((l) => (
              <div key={l.name} className="zed-level-card" style={{ '--lc': l.color, '--lb': l.border, '--lbg': l.bg }}>
                <div className="zed-level-badge" style={{ background: l.color }}>
                  <span>{l.emoji} {l.name}</span>
                </div>
                <div className="zed-level-score">{l.params}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>{l.effectiveCost}</div>
                <p className="zed-level-desc">{l.desc}</p>
                <ul className="zed-level-perks">
                  {l.perks.map((p) => <li key={p}>✓ {p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SUBSIDY TABLE ── */}
      <section className="section" style={{ background: 'var(--off-white)' }}>
        <div className="sec-inner" style={{ maxWidth: 820 }}>
          <div className="sec-tag">Government Subsidy</div>
          <h2 className="sec-title">Subsidy on Cost of Certification</h2>
          <p className="sec-lead" style={{ marginBottom: '2rem' }}>
            The Government of India provides substantial financial assistance to eligible MSMEs on ZED certification fees.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: 'var(--navy)', color: '#fff' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700 }}>Enterprise Type</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>Bronze</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>Silver</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700 }}>Gold</th>
                </tr>
              </thead>
              <tbody>
                {SUBSIDY.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '11px 16px', color: 'var(--navy)', fontWeight: 500 }}>{row.type}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: '#cd7f32', fontWeight: 700 }}>{row.bronze}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: '#6b7280', fontWeight: 700 }}>{row.silver}</td>
                    <td style={{ padding: '11px 16px', textAlign: 'center', color: '#b8860b', fontWeight: 700 }}>{row.gold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginTop: '1rem' }}>
            * A joining reward of ₹10,000 is provided on taking the ZED Pledge, effectively making Bronze certification
            free for MSMEs.
          </p>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="section" style={{ background: 'var(--blue-pale)' }}>
        <div className="sec-inner">
          <div className="sec-tag">Why Get Certified</div>
          <h2 className="sec-title">Benefits of ZED Certification</h2>
          <div className="zed-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div key={i} className="zed-benefit-card">
                <div className="zed-benefit-icon">{b.icon}</div>
                <h4>{b.title}</h4>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="section">
        <div className="sec-inner">
          <div className="sec-tag">How It Works</div>
          <h2 className="sec-title">ZED Certification Process</h2>
          <p className="sec-lead">A streamlined, paperless, single-window digital process — from pledge to certificate.</p>
          <div className="zed-steps-grid">
            {STEPS.map((s, i) => (
              <div key={i} className="zed-step-card">
                <div className="zed-step-num">{s.num}</div>
                <div className="zed-step-icon">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section" style={{ background: 'var(--off-white)' }}>
        <div className="sec-inner" style={{ maxWidth: 820 }}>
          <div className="sec-tag">FAQ</div>
          <h2 className="sec-title">Frequently Asked Questions</h2>
          <FaqBlock />
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <div className="cta-banner">
        <div className="cta-inner">
          <h2>Ready to start your ZED journey?</h2>
          <p>
            AQMCS team will guide your MSME from ZED Pledge to certificate issuance.
          </p>
          <button className="btn-blue" onClick={() => { setPage('Contact'); window.scrollTo(0, 0); }}>
            Talk to Our ZED Expert →
          </button>
        </div>
      </div>

    </main>
    </>
  );
}